import { apiClient } from '@/lib/apiClient';

export const MERCHANT_STATUSES = [
    'lead',
    'trial',
    'active',
    'at_risk',
    'churned',
    'suspended',
    'deleted',
] as const;

export type MerchantStatus = (typeof MERCHANT_STATUSES)[number];

export interface MerchantPlan {
    currency?: string;
    amount?: number;
    name: string;
    amount_aed: number | null;
    interval: string;
}

export type MerchantHealthBand = 'green' | 'amber' | 'red';
export type ChurnRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface MerchantCard {
    id: string;
    name: string;
    email: string | null;
    trn: string | null;
    business_type: string | null;
    emirate: string | null;
    status: MerchantStatus;
    status_changed_at: string | null;
    status_reason: string | null;
    plan: MerchantPlan | null;
    currency?: string;
    mrr?: number | null;
    mrr_aed: number | null;
    billing_source: 'component_subscription' | 'component_footprint_required' | 'legacy_plan' | 'none';
    billing_status: 'active' | 'payment_required' | 'past_due' | 'none';
    billing_issue: string | null;
    billable_branch_count: number;
    /** Legacy: average branch health score. */
    health_score: number | null;
    /** M4.7: tenant-level merchant health total (0–100). */
    merchant_health_total: number | null;
    /** M4.7: color band derived from total. */
    merchant_health_band: MerchantHealthBand | null;
    /** M4.8: churn risk level — null until first recompute. */
    churn_risk_level: ChurnRiskLevel | null;
    last_active_at: string | null;
    branch_count: number;
    staff_count: number;
    onboarding: {
        completion_pct: number;
        stalled_at_step: string | null;
        stalled_days: number | null;
        missing_milestones: string[];
        crm_workspace_id: string | null;
        crm_lead_id: string | null;
    };
    /**
     * Phase 15: tenants.retention_ends_at — the date a churned merchant's data
     * is deleted. Optional because the list endpoint does not project it yet.
     */
    retention_ends_at?: string | null;
}

export type CountsByStatus = Record<MerchantStatus, number>;

export const MERCHANT_SORT_FIELDS = [
    'name',
    'mrr',
    'health_score',
    'churn_risk',
    'last_active_at',
    'created_at',
] as const;

export type MerchantSortField = (typeof MERCHANT_SORT_FIELDS)[number];
export type MerchantSortDir = 'asc' | 'desc';

/** Business Account country a merchant was provisioned in. */
export const MERCHANT_REGION_CODES = ['AE', 'IN', 'SA'] as const;
export type MerchantRegionCode = (typeof MERCHANT_REGION_CODES)[number];

export interface ListMerchantsParams {
    status?: MerchantStatus;
    business_type?: string;
    emirate?: string;
    /** Business Account country filter; omitted means every region. */
    region?: MerchantRegionCode;
    health_score_below?: number;
    trial_ending_days?: number;
    onboarding_progress_min?: number;
    onboarding_progress_max?: number;
    stalled_days_min?: number;
    missing_milestone?: string;
    page?: number;
    limit?: number;
    search?: string;
    sort?: MerchantSortField;
    dir?: MerchantSortDir;
}

export interface ListMerchantsResponse {
    merchants: MerchantCard[];
    total: number;
    page: number;
    limit: number;
    counts_by_status: CountsByStatus;
}

export async function listMerchants(params: ListMerchantsParams): Promise<ListMerchantsResponse> {
    const { data } = await apiClient.get<{ data: ListMerchantsResponse }>('/merchants', {
        params,
    });
    return data.data;
}

export async function changeMerchantStatus(args: {
    merchantId: string;
    newStatus: MerchantStatus;
    reason: string;
}): Promise<void> {
    await apiClient.patch(
        `/merchants/${args.merchantId}/status`,
        {
            new_status: args.newStatus,
            reason: args.reason,
        },
        {
            headers: {
                'Idempotency-Key': crypto.randomUUID(),
            },
        },
    );
}

// ---------- M4.2 merchant detail (8 tabs) ----------

export type TabSource = 'live' | 'stub';
export interface TabEnvelope<T> {
    source: TabSource;
    data: T;
    reason?: string;
}

export const DETAIL_TABS = [
    'overview',
    'controls',
    'access',
    'subscription',
    // M5.6-real: platform-issued TAX INVOICEs with branded PDF +
    // UAE VAT. Distinct from the Stripe-pull list inside the
    // Subscription tab; this tab is the source of truth for
    // FTA-compliant invoice records.
    'invoices',
    'usage',
    'agents-ai',
    'audit',
    'support',
    'compliance',
    // M4.4: settings tab. FE-only composition; the backend returns a
    // delegated sentinel via the MCP composite tool but the page
    // hits M3.3 endpoints directly for the live merge.
    'settings',
    'branches',
    'staff',
    'integrations',
    // Per-tenant Stripe webhook event timeline for support diagnosis.
    'payment-events',
] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

export interface MerchantOverview {
    id: string;
    name: string;
    slug: string;
    status: MerchantStatus;
    status_changed_at: string | null;
    status_reason: string | null;
    /**
     * Legacy coarse classification from tenants.industryType. Kept because the
     * backend still returns it, but `business_type` below is the authoritative
     * value and the one the merchants list filters on — render that.
     */
    industry_type: string;
    /** Authoritative vertical from TenantSettings.businessType. */
    business_type: string | null;
    branch_count: number;
    staff_count: number;
    last_activity_at: string | null;
    plan_summary: MerchantPlan | null;
    created_at: string;
    deleted_at: string | null;
    /**
     * Phase 15: tenants.retention_ends_at — set while a churned merchant sits in
     * the read-only retention window, null otherwise. Drives the countdown chip.
     */
    retention_ends_at: string | null;
}

export interface MerchantSubscriptionDetail {
    plan: string | null;
    status: string | null;
    amount_aed: number | null;
    interval: string | null;
    start_date: string | null;
    end_date: string | null;
    is_active: boolean;
    total_outstanding_aed: number | null;
    last_invoice_at: string | null;
}

export interface MerchantAiUsageBreakdownRow {
    feature: string;
    calls: number;
    total_tokens: number;
    cost_usd: number;
}

export interface MerchantUsageDetail {
    window_days: 30;
    live: {
        active_staff_logins: number;
        orders_last_30d: number;
        llm_tokens_last_30d: number;
        ai_cost_usd_last_30d: number;
        ai_calls_last_30d: number;
        ocr_invoices_last_30d: number;
        ai_usage_by_feature: MerchantAiUsageBreakdownRow[];
    };
    stubs: {
        api_calls_last_30d: { source: 'stub'; reason: string };
    };
}

export interface MerchantSupportDetail {
    open_ticket_count: number;
    last_contact_at: string | null;
    satisfaction_score: number | null;
}

export interface MerchantComplianceDetail {
    trn: string | null;
    trn_valid: boolean;
    pdpl_consent: TabEnvelope<{ granted: boolean | null; granted_at: string | null }>;
    dsar_pending: TabEnvelope<{ count: number }>;
    last_audit_event_at: string | null;
}

export interface MerchantBranchesDetail {
    branches: Array<{
        id: string;
        name: string;
        code: string;
        status: string;
        branch_type: string;
        city: string | null;
        state: string | null;
        country: string | null;
        email: string | null;
        phone: string | null;
        trn: string | null;
        is_active: boolean;
        go_live_date: string | null;
        health_score: number | null;
        capabilities: {
            source: 'live' | 'missing';
            pos_enabled: boolean | null;
            inventory_enabled: boolean | null;
            khata_enabled: boolean | null;
            offline_mode_enabled: boolean | null;
            supplier_order_enabled: boolean | null;
            approval_gates: {
                discount: boolean | null;
                void: boolean | null;
                refund: boolean | null;
                transfer: boolean | null;
            };
        };
    }>;
    total: number;
}

export interface MerchantStaffDetail {
    staff: Array<{
        user_id: string;
        email: string;
        first_name: string;
        last_name: string;
        role: string;
        is_active: boolean;
        last_login_at: string | null;
        email_verified: boolean;
        mfa_enabled: boolean;
        linked_at: string | null;
    }>;
    total: number;
}

export interface MerchantIntegrationsDetail {
    whatsapp: { configured: boolean; source: 'live' };
    payment_gateway: { source: 'stub'; reason: string };
    accounting: { source: 'stub'; reason: string };
    delivery: { source: 'stub'; reason: string };
}

async function getTab<T>(merchantId: string, tab: DetailTab): Promise<TabEnvelope<T>> {
    const { data } = await apiClient.get<{ data: TabEnvelope<T> }>(
        `/merchants/${merchantId}/${tab}`,
    );
    return data.data;
}

export function getMerchantOverview(id: string): Promise<TabEnvelope<MerchantOverview>> {
    return getTab<MerchantOverview>(id, 'overview');
}
export function getMerchantSubscription(id: string): Promise<TabEnvelope<MerchantSubscriptionDetail>> {
    return getTab<MerchantSubscriptionDetail>(id, 'subscription');
}
export function getMerchantUsage(id: string): Promise<TabEnvelope<MerchantUsageDetail>> {
    return getTab<MerchantUsageDetail>(id, 'usage');
}
export function getMerchantSupport(id: string): Promise<TabEnvelope<MerchantSupportDetail>> {
    return getTab<MerchantSupportDetail>(id, 'support');
}
export function getMerchantCompliance(id: string): Promise<TabEnvelope<MerchantComplianceDetail>> {
    return getTab<MerchantComplianceDetail>(id, 'compliance');
}
export function getMerchantBranches(id: string): Promise<TabEnvelope<MerchantBranchesDetail>> {
    return getTab<MerchantBranchesDetail>(id, 'branches');
}
export function getMerchantStaff(id: string): Promise<TabEnvelope<MerchantStaffDetail>> {
    return getTab<MerchantStaffDetail>(id, 'staff');
}
export function getMerchantIntegrations(id: string): Promise<TabEnvelope<MerchantIntegrationsDetail>> {
    return getTab<MerchantIntegrationsDetail>(id, 'integrations');
}

// ---------- M4.3 merchant onboarding ----------

export const ONBOARDING_STEP_KEYS = [
    'profile',
    'first_branch',
    'tax_setup',
    'first_product',
    'first_staff',
    'pos_configured',
    'opening_stock',
    'first_recipe',
    'first_channel_connected',
    'channel_brand_mapping',
    // Grocery variant (backend onboarding.milestones.ts GROCERY_EXTRA_STEPS).
    'barcoded_items_loaded',
    'weighed_item_set',
    'expiry_tracking_on',
] as const;

export type OnboardingStepKey = (typeof ONBOARDING_STEP_KEYS)[number];

export interface OnboardingStep {
    key: OnboardingStepKey;
    order: number;
    completed_at: string | null;
    skipped: boolean;
    skipped_reason: string | null;
}

export interface MerchantOnboardingStatus {
    tenant_id: string;
    steps: OnboardingStep[];
    completion_pct: number;
    stalled_at_step: OnboardingStepKey | null;
    stalled_days: number | null;
}

export async function getMerchantOnboarding(id: string): Promise<MerchantOnboardingStatus> {
    const { data } = await apiClient.get<{ data: MerchantOnboardingStatus }>(
        `/merchants/${id}/onboarding`,
    );
    return data.data;
}

export async function completeOnboardingStep(args: {
    merchantId: string;
    step: OnboardingStepKey;
    reason?: string;
}): Promise<MerchantOnboardingStatus> {
    const { data } = await apiClient.post<{ data: MerchantOnboardingStatus }>(
        `/merchants/${args.merchantId}/onboarding/${args.step}/complete`,
        { reason: args.reason?.trim() || undefined },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

export async function skipOnboardingStep(args: {
    merchantId: string;
    step: OnboardingStepKey;
    reason: string;
}): Promise<MerchantOnboardingStatus> {
    const { data } = await apiClient.post<{ data: MerchantOnboardingStatus }>(
        `/merchants/${args.merchantId}/onboarding/${args.step}/skip`,
        { reason: args.reason },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

// ---------- M4.4 merchant settings tab ----------

export interface MerchantEffectiveSetting {
    key: string;
    value: unknown;
    category: string;
    description: string | null;
    editableBy: string[];
    source: 'platform' | 'tenant';
    overridden: boolean;
}

export interface MerchantEffectiveSettingsResponse {
    tenant_id: string;
    settings: MerchantEffectiveSetting[];
    total: number;
    overridden: number;
    by_category: Record<string, number>;
}

export async function getMerchantSettingsEffective(
    tenantId: string,
): Promise<MerchantEffectiveSettingsResponse> {
    const { data } = await apiClient.get<{ data: MerchantEffectiveSettingsResponse }>(
        `/merchants/${tenantId}/settings`,
    );
    return data.data;
}

export async function setMerchantSettingOverride(args: {
    tenantId: string;
    key: string;
    overrideValue: unknown;
    reason?: string;
}): Promise<{ setting: MerchantEffectiveSetting }> {
    const { data } = await apiClient.put<{ data: { setting: MerchantEffectiveSetting } }>(
        `/merchants/${args.tenantId}/settings/${encodeURIComponent(args.key)}`,
        { override_value: args.overrideValue, reason: args.reason },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

export async function clearMerchantSettingOverride(args: {
    tenantId: string;
    key: string;
}): Promise<{ cleared: boolean; tenant_id: string; key: string; platform_default: unknown }> {
    const { data } = await apiClient.delete<{
        data: { cleared: boolean; tenant_id: string; key: string; platform_default: unknown };
    }>(
        `/merchants/${args.tenantId}/settings/${encodeURIComponent(args.key)}`,
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

// ---------- M4.5 merchant suspension / reactivation ----------

export interface SuspensionSideEffects {
    sessions_invalidated: { count: number; reason?: string };
    api_keys_revoked: { count: number; reason?: string };
    email_queued: { queued: boolean; reason?: string };
    webhook_queued: { event: string };
}

export interface SuspensionNotice {
    id: string;
    action: 'suspended' | 'reactivated';
    reason: string;
    actor_id: string | null;
    billing_paused: boolean;
    notify_owner: boolean;
    email_sent_at: string | null;
    side_effects: Record<string, unknown>;
    created_at: string;
}

export interface SuspensionResponse {
    notice: SuspensionNotice;
    transition: { from: string; to: string; changed: boolean };
    side_effects: SuspensionSideEffects;
}

export interface SuspensionHistoryResponse {
    notices: SuspensionNotice[];
    total: number;
}

export async function suspendMerchant(args: {
    merchantId: string;
    reason: string;
    notifyOwner?: boolean;
    pauseBilling?: boolean;
}): Promise<SuspensionResponse> {
    const { data } = await apiClient.post<{ data: SuspensionResponse }>(
        `/merchants/${args.merchantId}/suspend`,
        {
            reason: args.reason,
            notify_owner: args.notifyOwner ?? true,
            pause_billing: args.pauseBilling ?? true,
        },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

export async function reactivateMerchant(args: {
    merchantId: string;
    reason: string;
    notifyOwner?: boolean;
}): Promise<SuspensionResponse> {
    const { data } = await apiClient.post<{ data: SuspensionResponse }>(
        `/merchants/${args.merchantId}/reactivate`,
        {
            reason: args.reason,
            notify_owner: args.notifyOwner ?? true,
        },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return data.data;
}

export async function listSuspensionHistory(
    merchantId: string,
): Promise<SuspensionHistoryResponse> {
    const { data } = await apiClient.get<{ data: SuspensionHistoryResponse }>(
        `/merchants/${merchantId}/history`,
    );
    return data.data;
}

// ---------- M4.6 merchant deletion (GDPR Art. 17) ----------

export interface DeleteMerchantExport {
    storage_path: string;
    signed_url: string | null;
    bytes: number;
    table_counts: Record<string, number>;
    expires_at: string;
}

export interface DeleteMerchantResponse {
    tenant_id: string;
    deleted_at: string;
    export?: DeleteMerchantExport;
    export_skipped?: { reason: string };
    owner_email_queued: { queued: boolean; reason?: string };
}

export async function deleteMerchant(args: {
    merchantId: string;
    reason: string;
    skipExport?: boolean;
}): Promise<DeleteMerchantResponse> {
    const { data } = await apiClient.delete<{ data: DeleteMerchantResponse }>(
        `/merchants/${args.merchantId}`,
        {
            data: {
                reason: args.reason,
                skip_export: args.skipExport ?? false,
                confirm: true,
            },
            headers: { 'Idempotency-Key': crypto.randomUUID() },
        },
    );
    return data.data;
}
